import React, { useState, useEffect, Component } from 'react';
import PropTypes from 'prop-types';
import { Transition } from 'react-transition-group';
import styled from 'styled-components';

import { PropTypesRepo } from '../functions/_propTypes';
import { withTheme } from '../core/theming';
import Div from './Div';
import Icon from './Icon';

const duration = 200;

const defaultAccordionBodyStyle = {
  transition: `height ${duration}ms ease-out`,
  height: 0,
};

const transitionAccordionBodyStyles = ({ h }) => ({
  entering: { height: 0, overflow: 'hidden', border: 'none' },
  entered: { height: h, paddingTop: '12px' },
  exiting: { height: h, paddingTop: '12px' },
  exited: { height: 0, overflow: 'hidden', border: 'none' },
});

const Title = styled(Div)`
  ${(props) => props.targetHover && `:hover { border-color: ${props.theme.colors[props.focusBorderColor]}}`}
  ${(props) => props.targetHover && ':hover > div:last-child{ opacity: 1; transform: scaleY(1);}'}
`;

const AccordionSection = ({
  isOpen: isOpenProps,
  onOpen,
  openSuffix,
  closeSuffix,
  title,
  children,
  titleStyle,
  contentStyle,
  titleStyleExtra,
  contentStyleExtra,
  titlePrefix,
  titleSuffix,
}) => {
  const [isOpen, setIsOpen] = useState(isOpenProps);
  useEffect(() => {
    setIsOpen(isOpenProps);
  }, [isOpenProps]);
  return (
    <Transition
      in={isOpen}
      timeout={duration}
      onExited={() => {
        setIsOpen(false);
      }}
    >
      {(state) => (
        <Div
          border={{
            b: '1px solid',
          }}
          borderColor="gray400"
        >
          <Title
            isOpen={isOpen}
            onClick={() => {
              setIsOpen(!isOpen);
              onOpen();
            }}
            {...titleStyle}
            {...titleStyleExtra}
            bg={isOpen ? titleStyle.hoverBg : titleStyle.bg}
          >
            {titlePrefix}
            {title}
            <Div d="flex" align="center">
              {titleSuffix}
              {isOpen ? openSuffix : closeSuffix}
            </Div>
          </Title>
          <Div
            style={{
              ...defaultAccordionBodyStyle,
              ...transitionAccordionBodyStyles({
                h: contentStyleExtra.h || contentStyle.h, // eslint-disable-line react/prop-types
              })[state],
            }}
            {...contentStyle}
            {...contentStyleExtra}
          >
            {children}
          </Div>
        </Div>
      )}
    </Transition>
  );
};

AccordionSection.propTypes = {
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  openSuffix: PropTypes.node,
  closeSuffix: PropTypes.node,
  focusBorderColor: PropTypesRepo.focusBorderColor,
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  onOpen: PropTypes.func.isRequired,
  titleStyle: PropTypes.shape({
    bg: PropTypesRepo.bg,
    hoverBg: PropTypesRepo.hoverBg,
  }).isRequired,
  contentStyle: PropTypes.shape({
    h: PropTypesRepo.h,
  }).isRequired,
  titleStyleExtra: PropTypes.shape({}).isRequired,
  contentStyleExtra: PropTypes.shape({}).isRequired,
  titlePrefix: PropTypes.node,
  titleSuffix: PropTypes.node,
};

AccordionSection.defaultProps = {
  isOpen: false,
  openSuffix: <Icon name="UpArrow" size="18px" m={{ l: '16px' }} color="medium" />,
  closeSuffix: <Icon name="DownArrow" size="18px" m={{ l: '16px' }} color="medium" />,
  focusBorderColor: 'gray800',
  children: null,
  titlePrefix: null,
  titleSuffix: null,
};

class Accordion extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedIndex: props.selectedIndex,
    };
  }

  render() {
    const { sections, titleStyle, contentStyle, containerStyle, titleStyleExtra, contentStyleExtra } = this.props;
    const { selectedIndex } = this.state;
    return (
      <Div border="1px solid" rounded="md" borderColor="gray400" {...containerStyle}>
        {sections.map(({ titlePrefix, title, titleSuffix, content }, index) => (
          <AccordionSection
            title={title}
            isOpen={selectedIndex === index}
            onOpen={() => {
              this.setState({
                selectedIndex: index,
              });
            }}
            titleStyle={titleStyle}
            titleStyleExtra={titleStyleExtra}
            contentStyle={contentStyle}
            contentStyleExtra={contentStyleExtra}
            titlePrefix={titlePrefix}
            titleSuffix={titleSuffix}
          >
            {content}
          </AccordionSection>
        ))}
      </Div>
    );
  }
}

Accordion.propTypes = {
  selectedIndex: PropTypes.number,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      content: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
      titlePrefix: PropTypes.node,
      titleSuffix: PropTypes.node,
    })
  ).isRequired,
  titleStyle: PropTypes.shape({
    bg: PropTypesRepo.bg,
    hoverBg: PropTypesRepo.hoverBg,
  }),
  contentStyle: PropTypes.shape({
    h: PropTypesRepo.h,
  }),
  containerStyle: PropTypes.shape({}),
  titleStyleExtra: PropTypes.shape({}),
  contentStyleExtra: PropTypes.shape({}),
};

Accordion.defaultProps = {
  selectedIndex: 0,
  contentStyle: {
    w: '100%',
    h: '100px',
    p: { x: '12px' },
    border: {
      t: '1px solid',
    },
    borderColor: 'gray200',
    rounded: '0',
    textSize: 'body',
    bg: 'white',
  },
  contentStyleExtra: {},
  titleStyle: {
    w: '100%',
    d: 'flex',
    align: 'center',
    textColor: 'medium',
    textWeight: '500',
    justify: 'space-between',
    p: { x: '12px' },
    h: '48px',
    textSize: 'title',
    bg: 'white',
    hoverBg: 'gray100',
    cursor: 'pointer',
  },
  titleStyleExtra: {},
  containerStyle: {},
};

export default withTheme(Accordion);
